import React, { useEffect, useState } from "react";
import StickyTitle from "../components/StickyTitle";
import {
  parseISO,
  isWithinInterval,
  isSameDay,
  isSameMonth,
  isSameYear,
  startOfWeek,
} from "date-fns";
import { FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import profit from "../../assets/profit.png";
import loss from "../../assets/expenses.png";
import sales from "../../assets/tag.png";
import {
  BarChart,
  Bar,
  YAxis,
  XAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { getMonthlyTotals } from "../../utils/helperService";

const DashboardPage = () => {
  let [invoiceData, setInvoiceData] = useState([]);
  const [chartGrouping, setChartGrouping] = useState("byDay");
  let [itemData, setItemData] = useState([]);
  let [expenseData, setExpenseData] = useState([]);
  const [selectedDateFilter, setSelectedDateFilter] = useState("all");

  useEffect(() => {
    const fetchAllInvoices = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/invoices/all_invoices`
        );
        const responseData = await response.json();
        if (responseData) {
          setInvoiceData(responseData.invoice);
        }
      } catch (error) {
        console.error("Error fetching invoices:", error.message);
      }
    };

    const fetchAllExpenses = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/expenses/all_expenses`
        );
        const responseData = await response.json();
        if (responseData) {
          setExpenseData(responseData.expense);
        }
      } catch (error) {
        console.error("Error fetching expenses:", error.message);
      }
    };

    const fetchAllItemServices = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/itemservices/all_itemservices`
        );
        const responseData = await response.json();
        // Separate data for "item" and "service" types
        const itemData = responseData?.itemservice?.filter(
          (item) => item.type === "Item"
        );
        setItemData(itemData);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchAllItemServices();
    fetchAllInvoices();
    fetchAllExpenses();
  }, []);

  const getItemPurchaseTotals = () => {
    const purchaseTotals = {
      total: 0,
      purchases: [],
    };

    const filteredPurchases = itemData.filter((item) =>
      isValidDateFilter(selectedDateFilter, item.purchase_date)
    );

    filteredPurchases.forEach((item) => {
      const { itemservice, incomeamount, currentstock, purchase_date } = item;

      purchaseTotals.total += parseFloat(incomeamount * currentstock);
      purchaseTotals.purchases.push({
        id: item.id,
        title: itemservice,
        total: parseFloat(incomeamount),
        purchasedatedate: purchase_date,
      });
    });

    return purchaseTotals;
  };

  const getExpenseTotals = () => {
    const expenseTotals = {
      totalExpenses: 0,
      expenses: [],
    };

    const filteredExpenses = expenseData.filter((expense) =>
      isValidDateFilter(selectedDateFilter, expense.expensedate)
    );

    const itemspurchase = getItemPurchaseTotals();

    filteredExpenses.forEach((expense) => {
      const { title, amount } = expense;

      expenseTotals.totalExpenses += parseFloat(amount);
      expenseTotals.expenses.push({
        id: expense.id,
        title: title,
        total: parseFloat(amount),
        expensedate: expense.expensedate,
      });
    });
    expenseTotals.totalExpenses += itemspurchase.total;

    return expenseTotals;
  };

  const getProfitTotals = () => {
    const profitTotals = {
      totalProfit: 0,
      invoices: [],
    };

    const filteredInvoices = invoiceData.filter((invoice) =>
      isValidDateFilter(selectedDateFilter, invoice.issuedate)
    );

    const filteredExpenses = expenseData.filter((expense) =>
      isValidDateFilter(selectedDateFilter, expense.expensedate)
    );

    // Calculate total income from invoices
    profitTotals.totalProfit += filteredInvoices.reduce(
      (total, invoice) => total + parseFloat(invoice.total),
      0
    );

    profitTotals.invoices.push(
      ...filteredInvoices.map((invoice) => ({
        id: invoice.id,
        invoice: invoice.invoicenumber,
        total: parseFloat(invoice.total),
        issuedate: invoice.issuedate,
      }))
    );

    // Deduct total expenses
    profitTotals.totalProfit -= filteredExpenses.reduce(
      (total, expense) => total + parseFloat(expense.amount),
      0
    );

    const itemspurchase = getItemPurchaseTotals();
    profitTotals.totalProfit -= itemspurchase.total;

    profitTotals.invoices.push(
      ...filteredExpenses.map((expense) => ({
        id: expense.id,
        title: expense.title,
        total: -parseFloat(expense.amount), // Expenses contribute negatively to profit
        expensedate: expense.expensedate,
      }))
    );

    return profitTotals;
  };

  const getSalesTotal = () => {
    const salesTotal = {
      totalSales: 0,
      invoices: [],
    };

    const filteredInvoices = invoiceData.filter((invoice) =>
      isValidDateFilter(selectedDateFilter, invoice.issuedate)
    );

    salesTotal.totalSales += filteredInvoices.reduce(
      (total, invoice) => total + parseFloat(invoice.total),
      0
    );

    salesTotal.invoices.push(
      ...filteredInvoices.map((invoice) => ({
        id: invoice.id,
        title: invoice.invoicenumber,
        total: parseFloat(invoice.total),
        issuedate: invoice.issuedate,
      }))
    );

    return salesTotal;
  };

  const isValidDateFilter = (filter, date) => {
    const currentDate = new Date();
    const parsedDate = parseISO(date);

    switch (filter) {
      case "today":
        return isSameDay(parsedDate, currentDate);

      case "thisWeek":
        const startOfWeek1 = startOfWeek(currentDate);
        return isWithinInterval(parsedDate, {
          start: startOfWeek1,
          end: currentDate,
        });

      case "thisMonth":
        return isSameMonth(parsedDate, currentDate);

      case "thisYear":
        return isSameYear(parsedDate, currentDate);

      default:
        return true;
    }
  };

  const handleDateFilterChange = (event) => {
    setSelectedDateFilter(event.target.value);
  };

  const expenseObj = getExpenseTotals();
  const profitObj = getProfitTotals();
  const salesObj = getSalesTotal();

  const data = getMonthlyTotals(
    invoiceData,
    expenseData,
    itemData,
    chartGrouping
  );

  const handleChartGroupingChange = (event) => {
    setChartGrouping(event.target.value);
  };

  return (
    <>
      <StickyTitle title="Dashboard" no_button={true} />
      <div className="px-4 lg:px-8 py-4 lg:py-10 flex flex-col gap-10 overflow-x-auto">
        <div className=" px-5 sm:px-8 xl:px-20 py-1.5 sm:py-2 md:py-5 lg:py-6 bg-white text-black rounded-[30px] flex xl:flex-row flex-col items-center justify-center w-fit xl:w-full gap-0 xl:gap-5 shadow-lg mx-auto ">
          <div className="">
            <FormControl>
              <InputLabel> Filter by time </InputLabel>
              <Select
                value={selectedDateFilter}
                onChange={handleDateFilterChange}
                sx={{
                  width: "100px",
                  boxShadow: "none",
                  ".MuiOutlinedInput-notchedOutline": { border: 0 },
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="thisWeek">This Week</MenuItem>
                <MenuItem value="thisMonth">This Month</MenuItem>
                <MenuItem value="thisYear">This Year</MenuItem>
              </Select>
            </FormControl>
          </div>
          <div className=" pr-2 md:pr-3 lg:pr-5 py-1 md:py-2 lg:py-2 flex items-center gap-2 border-b xl:border-b-0 xl:border-r border-gray-400">
            <div className=" p-3 xl:p-4 rounded-full bg-[#2F80ED] flex items-center justify-center]">
              <img className=" w-10 min-w-[20px]" src={profit} alt="user-1" />
            </div>
            <div className=" flex flex-col">
              <h2 className=" text-sm md:text-base lg:text-lg font-semibold">
                Total Profit
              </h2>
              <p className=" text-lg md:text-xl font-bold">
                {parseFloat(profitObj.totalProfit || 0).toLocaleString(
                  "en-US",
                  { maximumFractionDigits: 0 }
                )}
                <span className=" ml-2">QAR</span>
              </p>
            </div>
          </div>
          <div className=" pr-2 md:pr-3 lg:pr-5 py-1 md:py-2 lg:py-2 flex items-center gap-2 border-b xl:border-b-0 xl:border-r border-gray-400">
            <div className=" p-3 xl:p-4 rounded-full bg-[#2F80ED] flex items-center justify-center]">
              <img className=" w-10 min-w-[24px]" src={sales} alt="user-1" />
            </div>
            <div className=" flex flex-col">
              <h2 className=" text-sm md:text-base lg:text-lg font-semibold">
                Total Sales
              </h2>
              <p className=" text-lg md:text-xl font-bold">
                {parseFloat(salesObj.totalSales || 0).toLocaleString("en-US", {
                  maximumFractionDigits: 0,
                })}
                <span className=" ml-2">QAR</span>
              </p>
            </div>
          </div>
          <div className=" pr-2 md:pr-3 lg:pr-5 py-1 md:py-2 lg:py-2 flex items-center gap-2 xl:border-none border-b xl:border-b-0 border-gray-400">
            <div className=" p-3 xl:p-4 rounded-full bg-[#2F80ED] flex items-center justify-center]">
              <img className=" w-10 min-w-[24px]" src={loss} alt="user-1" />
            </div>
            <div className=" flex flex-col">
              <h2 className=" text-sm md:text-base lg:text-lg font-semibold">
                Total Expenses
              </h2>
              <p className=" text-lg md:text-xl font-bold">
                {parseFloat(expenseObj.totalExpenses || 0).toLocaleString(
                  "en-US",
                  { maximumFractionDigits: 0 }
                )}
                <span className=" ml-2">QAR</span>
              </p>
            </div>
          </div>
        </div>

        <div className=" flex lg:flex-row flex-col lg:items-start items-center gap-3">
          <div>
            <FormControl>
              <InputLabel>Chart Grouping: </InputLabel>
              <Select
                value={chartGrouping}
                onChange={handleChartGroupingChange}
                sx={{
                  width: "130px",
                  boxShadow: "none",
                  ".MuiOutlinedInput-notchedOutline": { border: 0 },
                }}
              >
                <MenuItem value="byMonth">By Month</MenuItem>
                <MenuItem value="byDay">By Day</MenuItem>
                <MenuItem value="byYear">By Year</MenuItem>
              </Select>
            </FormControl>
          </div>
          <ResponsiveContainer width="80%" height={500}>
            <BarChart width={730} height={250} data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="timePeriod" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="totalExpenses" fill="#8884d8" />
              <Bar dataKey="totalProfit" fill="#82ca9d" />
              <Bar dataKey="totalSales" fill="#82c32d" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );
};

export default DashboardPage;
